import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useDownholeSettings, useUpdateDownholeSettings } from '../../hooks/useDownholeSettings';
import { standbyPctFromInput, standbyPctToInput } from '../../lib/downhole-units';
import type { DhSettingsWrite } from '../../types/downhole';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';

/*
 * Settings → Operations → Downhole (GET/PUT /downhole/settings, api#1052).
 *
 * The Directional & MWD switch (`dd_mwd_enabled`), off until an admin turns
 * it on, and the open-run alert (`open_run_alert_hours`, api#1126): how long a
 * run may stay in the hole before the Action Queue says so. The alert applies
 * with the mode on or off, so its field is always shown. A company with no dh_settings row reads `false` from the API,
 * which is the real default, so the page shows it as off rather than as a gap.
 *
 * GET is DOWNHOLE_READ_ROLES (admin, office_staff, dispatcher, field_worker);
 * `owner` is not on that list and gets the refusal notice. PUT is non-strict
 * requireRole('admin'), which is exactly what useCanWriteCompanySettings
 * mirrors, so everyone else who can read sees the switch disabled.
 *
 * A 400 carries `{ code, error }`; the error is shown as the API wrote it.
 */
export default function DownholeSettingsPage() {
  const { hasVertical } = useAuth();
  const canWrite = useCanWriteCompanySettings();
  const query = useDownholeSettings();
  const save = useUpdateDownholeSettings();
  const failure = queryLoadFailure(query);
  const settings = query.data?.data ?? null;

  const [ddMwd, setDdMwd] = useState(false);
  const [standby, setStandby] = useState('');
  const [alertHours, setAlertHours] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setDdMwd(settings.dd_mwd_enabled === true);
      setStandby(standbyPctToInput(settings.default_standby_pct));
      // An absent value (an older API) is left blank, never shown as the default.
      setAlertHours(typeof settings.open_run_alert_hours === 'number' ? String(settings.open_run_alert_hours) : '');
    }
  }, [settings]);

  // Other tenants are not offered the tab, but a direct visit still renders.
  if (!hasVertical('downhole_tools')) {
    return (
      <div className="px-6 py-4">
        <p className="text-sm text-fieldiq-muted">Downhole settings apply only to a company running Downhole Tools.</p>
      </div>
    );
  }

  if (failure) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice
          error={queryFailureError(query)}
          kind={failure}
          subject="downhole settings"
          allowed="admins, office staff, dispatchers and field workers"
        />
      </div>
    );
  }

  if (query.isLoading || !settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading downhole settings" />
      </div>
    );
  }

  const standbyBefore = standbyPctToInput(settings.default_standby_pct);
  const standbyChanged = ddMwd && standby.trim() !== standbyBefore;
  const alertBefore = typeof settings.open_run_alert_hours === 'number' ? String(settings.open_run_alert_hours) : '';
  const alertChanged = alertHours.trim() !== alertBefore;
  const dirty = ddMwd !== settings.dd_mwd_enabled || standbyChanged || alertChanged;

  const onSave = () => {
    setError(null);
    // Only what moved. The standby % is sent only while the mode is on (or is
    // being turned on in the same save): the API refuses it otherwise.
    const body: DhSettingsWrite = {};
    if (ddMwd !== settings.dd_mwd_enabled) body.dd_mwd_enabled = ddMwd;
    if (standbyChanged) {
      const fraction = standbyPctFromInput(standby);
      if (fraction === undefined) return setError('Default standby must be a percent above 0 and at most 100.');
      body.default_standby_pct = fraction;
    }
    if (alertChanged) {
      const raw = alertHours.trim();
      const hours = Number(raw);
      if (!/^\d+$/.test(raw) || hours < 1 || hours > 2000) {
        return setError('The open-run alert must be a whole number of hours from 1 to 2000.');
      }
      body.open_run_alert_hours = hours;
    }
    save.mutate(
      body,
      {
        onSuccess: () => toast.success('Downhole settings saved'),
        onError: (err: any) => setError(err?.response?.data?.error || 'The settings could not be saved.'),
      },
    );
  };

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin can change downhole settings.</p>
        )}

        {error && (
          <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}

        <section className="space-y-3">
          <div className="flex items-center justify-between bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <div className="flex-1 mr-4">
              <p className="text-sm font-medium text-white">Directional &amp; MWD</p>
              <p className="text-xs text-fieldiq-muted mt-0.5">Turns on Directional &amp; MWD work for this company. Off until an admin turns it on.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={ddMwd}
              aria-label="Directional & MWD"
              disabled={!canWrite || save.isPending}
              onClick={() => setDdMwd((v) => !v)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-60 ${ddMwd ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${ddMwd ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </section>

        <section className="space-y-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-white">Open run alert (hours)</span>
              <span className="block text-xs text-fieldiq-muted mt-0.5">
                A run still in the hole after this many hours shows in the Action Queue. Whole hours, 1 to 2000.
              </span>
              <input
                className="mt-2 w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed"
                inputMode="numeric"
                value={alertHours}
                disabled={!canWrite || save.isPending}
                onChange={(e) => setAlertHours(e.target.value)}
                aria-label="Open run alert (hours)"
                placeholder="—"
              />
            </label>
          </div>
        </section>

        {ddMwd && (
          <section className="space-y-3">
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
              <label className="block max-w-xs">
                <span className="text-sm font-medium text-white">Default standby (%)</span>
                <span className="block text-xs text-fieldiq-muted mt-0.5">
                  The share of a tool's day rate billed on a standby day, when its rate sets no standby of its own. Leave blank for none.
                </span>
                <input
                  className="mt-2 w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed"
                  inputMode="decimal"
                  value={standby}
                  disabled={!canWrite || save.isPending}
                  onChange={(e) => setStandby(e.target.value)}
                  aria-label="Default standby percent"
                  placeholder="Not set"
                />
              </label>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
